import type {
	IGetCargoByTrip,
	IGetCargoInTripDetails,
	IGetActiveTrip,
	IGetHeaderInfo,
	IGetTripById,
	IGetTripOnTheWay,
	ITrip,
	TCargoTypeResults
} from '~~/shared/interfaces/truck';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { getConnectionStatus } from '~/helpers/network';
import { useProfileStore } from '~/store/profile.store';
import { getCachedGet, getGetCacheIdentity } from '~/helpers/offlineRequestQueue';
import { useAuthToken } from '~/composables/useAuthToken';
import {
	getTruckerBackgroundCache,
	getTruckerCargoDetailsCacheKey,
	getTruckerCargosCacheKey,
	getTruckerSntReportCacheKey,
	getTruckerTripDetailsCacheKey,
	getTruckerTripsCacheKey,
	saveTruckerBackgroundCache
} from '~/helpers/truckerBackgroundCache';

type TCachedActiveTripResponse = IGetActiveTrip | IGetTripOnTheWay;

const isTrip = (value: unknown): value is ITrip =>
	Boolean(value && typeof value === 'object' && 'id' in value && typeof value.id === 'string');

const isValidActiveTripResponse = (response: TCachedActiveTripResponse | null): response is TCachedActiveTripResponse =>
	Boolean(
		response?.isSuccessful &&
			(response.data === undefined ||
				response.data === null ||
				isTrip(response.data) ||
				(Array.isArray(response.data) && response.data.every(isTrip)))
	);

const isValidTripByIdResponse = (response: IGetTripById | null): response is IGetTripById =>
	Boolean(
		response?.isSuccessful &&
			(response.data === undefined || response.data === null || isTrip(response.data))
	);

const getActiveTrips = (response: TCachedActiveTripResponse): ITrip[] =>
	Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];

const isValidCargosResponse = (response: IGetCargoByTrip | null): response is IGetCargoByTrip =>
	Boolean(
		response?.isSuccessful &&
			response.data &&
			typeof response.data === 'object' &&
			!Array.isArray(response.data) &&
			Object.values(response.data).every(Array.isArray)
	);

const isValidCargoDetailsResponse = (
	response: IGetCargoInTripDetails | null
): response is IGetCargoInTripDetails =>
	Boolean(response?.isSuccessful && response.data && typeof response.data === 'object');

interface ISntReportResponse {
	isSuccessful: boolean;
	data: string;
}

const isValidSntReportResponse = (response: ISntReportResponse | null): response is ISntReportResponse =>
	Boolean(response?.isSuccessful && typeof response.data === 'string');

export const useLongHaulTripStore = defineStore('longHaulTrip', () => {
	const headerInfo = ref<IGetHeaderInfo | null>(null);
	const tripsOnTheWay = ref<ITrip[]>([]);
	const abortControllers: Record<string, AbortController> = {};
	const getHeaderCacheKey = () => {
		const profileId = useProfileStore().profileInfo?.Id;
		return profileId ? `long-haul-header-${String(profileId)}` : null;
	};
	const loading = reactive({
		getTripsOnTheWay: false
	});

	function $reset() {
		headerInfo.value = null;
	}

	const clearTripsOnTheWayById = (id: string) => {
		tripsOnTheWay.value = tripsOnTheWay.value.filter((trip) => trip.id !== id);
	};

	const normalizeHeaderInfo = (value: unknown): IGetHeaderInfo | null => {
		let candidate = value;
		while (
			candidate &&
			typeof candidate === 'object' &&
			'data' in candidate &&
			candidate.data &&
			typeof candidate.data === 'object'
		) {
			candidate = candidate.data;
		}

		return candidate && typeof candidate === 'object' && ('name' in candidate || 'car' in candidate)
			? (candidate as IGetHeaderInfo)
			: null;
	};

	const readCachedHeaderInfo = async () => {
		const key = getHeaderCacheKey();
		if (import.meta.server) return null;

		let value: string | null = null;
		if (key) {
			try {
				value = Capacitor.isNativePlatform() ? (await Preferences.get({ key })).value : localStorage.getItem(key);
			} catch (error) {
				console.error('Failed to read trucker header from Preferences:', error);
			}
			if (!value) {
				try {
					value = localStorage.getItem(key);
				} catch (error) {
					console.error('Failed to read trucker header from local storage:', error);
				}
			}
		}

		if (value) {
			try {
				const cached = normalizeHeaderInfo(JSON.parse(value));
				if (cached) return cached;
			} catch (error) {
				console.error('Failed to parse cached trucker header:', error);
			}
		}

		try {
			const token = await useAuthToken().getAuthToken();
			const identity = await getGetCacheIdentity('/api/LongHaulTrip/get-info-head', {}, token);
			const cachedGet = await getCachedGet(identity.key);
			return normalizeHeaderInfo(cachedGet?.response);
		} catch (error) {
			console.error('Failed to read cached trucker GET response:', error);
			return null;
		}
	};

	const saveCachedHeaderInfo = async (value: IGetHeaderInfo) => {
		const key = getHeaderCacheKey();
		if (!key || import.meta.server) return;

		const serializedValue = JSON.stringify(value);
		if (Capacitor.isNativePlatform()) {
			try {
				await Preferences.set({ key, value: serializedValue });
				return;
			} catch (error) {
				console.error('Failed to persist trucker header in Preferences:', error);
			}
		}

		localStorage.setItem(key, serializedValue);
	};

	const markCustomsPassed = async (payload: { tripId: string }) => {
		const { data, status } = await useCustomFetch<IGetTripOnTheWay>(
			'/api/LongHaulTrip/mark-customs-passed',
			{
				method: 'post',
				query: payload,
				timeout: 4000,
				watch: false
			},
			{ isSilent: false }
		);

		if (status.value === 'success' && data.value) {
		}

		return { data: data.value, status: status.value };
	};

	const setArriveTrip = async (payload: { tripId: string }) => {
		const { data, status } = await useCustomFetch<IGetTripOnTheWay>(
			'/api/LongHaulTrip/set-arrive-trip',
			{
				method: 'post',
				query: payload,
				timeout: 4000,
				watch: false
			},
			{ isSilent: false }
		);

		if (status.value === 'success' && data.value) {
		}

		return { data: data.value, status: status.value };
	};

	const setDeliveredCargo = async (payload: { cargoId: string; tripId: string; paymentAmount: number | null }) => {
		const { data, status } = await useCustomFetch<unknown>(
			'/api/LongHaulTrip/set-delivered-cargo',
			{
				method: 'post',
				query: payload,
				timeout: 4000,
				watch: false
			},
			{ isSilent: false }
		);

		return { data: data.value, status: status.value };
	};

	const abortRequest = (key: string) => {
		if (abortControllers[key]) {
			abortControllers[key].abort();
		}
		abortControllers[key] = new AbortController();
		return abortControllers[key].signal;
	};

	const cancelRequest = (key: string) => {
		abortControllers[key]?.abort();
		delete abortControllers[key];
	};

	const getTripsOnTheWay = async (isDisabledErrroMessage = false) => {
		const signal = abortRequest('getTripsOnTheWay');
		loading.getTripsOnTheWay = true;
		try {
			const isOnline = await getConnectionStatus();
			const cachedTrips = isOnline
				? null
				: await getTruckerBackgroundCache<TCachedActiveTripResponse>(getTruckerTripsCacheKey());
			if (!isOnline && isValidActiveTripResponse(cachedTrips)) {
				tripsOnTheWay.value = getActiveTrips(cachedTrips);
				return { data: tripsOnTheWay.value, error: null, status: 'success' };
			}

			const { data, status, error } = await useCustomFetch<IGetActiveTrip>(
				'/api/LongHaulTrip/get-active-trip',
				{
					query: {},
					signal,
					timeout: 4000,
					watch: false,
					server: false
				},
				{
					isDisabledErrroMessage: isDisabledErrroMessage
				}
			);

			if (status.value === 'success' && isValidActiveTripResponse(data.value)) {
				tripsOnTheWay.value = getActiveTrips(data.value);
				try {
					await saveTruckerBackgroundCache(getTruckerTripsCacheKey(), data.value);
				} catch (cacheError) {
					console.error('Failed to save trucker trips background cache:', cacheError);
				}
			} else {
				const fallbackTrips =
					cachedTrips || (await getTruckerBackgroundCache<TCachedActiveTripResponse>(getTruckerTripsCacheKey()));
				if (isValidActiveTripResponse(fallbackTrips)) {
					tripsOnTheWay.value = getActiveTrips(fallbackTrips);
					return { data: tripsOnTheWay.value, error: null, status: 'success' };
				}
			}

			const responseIsValid = isValidActiveTripResponse(data.value);
			if (status.value !== 'success' || !responseIsValid) {
				console.error('Failed to load trips on the way:', {
					status: status.value,
					error: error.value,
					hasResponse: data.value !== null,
					isSuccessful: data.value?.isSuccessful,
					hasActiveTrip: isTrip(data.value?.data)
				});
			}

			return {
				data: tripsOnTheWay.value,
				error: error.value,
				status: status.value === 'success' && !responseIsValid ? 'error' : status.value
			};
		} finally {
			loading.getTripsOnTheWay = false;
		}
	};

	const getTripById = async (tripId: string, isDisabledErrroMessage = false) => {
		const cacheKey = getTruckerTripDetailsCacheKey(tripId);
		const isOnline = await getConnectionStatus();
		const cachedTrip = isOnline ? null : await getTruckerBackgroundCache<IGetTripById>(cacheKey);
		if (isValidTripByIdResponse(cachedTrip)) {
			return { data: cachedTrip, status: 'success' };
		}

		const signal = abortRequest(`getTripById:${tripId}`);
		const { data, status } = await useCustomFetch<IGetTripById>(
			'/api/LongHaulTrip/get-trip-by-id',
			{
				query: { tripId },
				signal,
				timeout: 4000,
				watch: false,
				server: false
			},
			{ isDisabledErrroMessage }
		);

		if (status.value === 'success' && isValidTripByIdResponse(data.value)) {
			try {
				await saveTruckerBackgroundCache(cacheKey, data.value);
			} catch (error) {
				console.error('Failed to save trucker trip details cache:', error);
			}
			return { data: data.value, status: 'success' };
		}

		const fallbackTrip =
			cachedTrip || (await getTruckerBackgroundCache<IGetTripById>(cacheKey));
		if (isValidTripByIdResponse(fallbackTrip)) {
			return { data: fallbackTrip, status: 'success' };
		}

		return {
			data: data.value,
			status: status.value === 'success' && !isValidTripByIdResponse(data.value) ? 'error' : status.value
		};
	};

	const getHeaderInfo = async () => {
		if (import.meta.server) return { data: headerInfo.value, status: 'pending' };

		const cachedHeaderInfo = await readCachedHeaderInfo();
		if (cachedHeaderInfo) headerInfo.value = cachedHeaderInfo;
		if (!(await getConnectionStatus())) {
			return { data: headerInfo.value, status: headerInfo.value ? 'success' : 'error' };
		}

		const signal = abortRequest('getHeaderInfo');
		const { data, status } = await useCustomFetch<IGetHeaderInfo>(
			'/api/LongHaulTrip/get-info-head',
			{
				query: {},
				signal,
				timeout: 4000,
				watch: false
			},
			{ isSilent: true }
		);

		if (status.value === 'success' && data.value) {
			const normalizedHeaderInfo = normalizeHeaderInfo(data.value);
			if (normalizedHeaderInfo) {
				headerInfo.value = normalizedHeaderInfo;
				try {
					await saveCachedHeaderInfo(normalizedHeaderInfo);
				} catch (error) {
					console.error('Failed to cache trucker header:', error);
				}
			}
		}

		return { data: headerInfo.value, status: status.value };
	};

	const getSntReportByTrip = async (
		payload: { tripId: string; sntReport: 'Simple' | 'Detailed' },
		isSilent = false
	) => {
		const cacheKey = getTruckerSntReportCacheKey(payload.tripId, payload.sntReport);
		const isOnline = await getConnectionStatus();
		const cachedReport = isOnline ? null : await getTruckerBackgroundCache<ISntReportResponse>(cacheKey);
		if (isValidSntReportResponse(cachedReport)) {
			return { data: cachedReport, status: 'success' };
		}

		const signal = abortRequest(`getSntReportByTrip:${payload.tripId}:${payload.sntReport}`);
		const { data, status } = await useCustomFetch<ISntReportResponse>(
			'/api/LongHaulTrip/get-snt-report-by-trip',
			{
				query: payload,
				signal,
				timeout: 4000,
				watch: false
			},
			{ isSilent }
		);

		if (status.value === 'success' && isValidSntReportResponse(data.value)) {
			try {
				await saveTruckerBackgroundCache(cacheKey, data.value);
			} catch (error) {
				console.error('Failed to save trucker SNT report cache:', error);
			}
		} else {
			const fallbackReport =
				cachedReport || (await getTruckerBackgroundCache<ISntReportResponse>(cacheKey));
			if (isValidSntReportResponse(fallbackReport)) {
				return { data: fallbackReport, status: 'success' };
			}
		}

		return {
			data: data.value,
			status: status.value === 'success' && !isValidSntReportResponse(data.value) ? 'error' : status.value
		};
	};

	const prefetchSntReportsForTrips = async (trips: ITrip[]) => {
		const requests = trips.flatMap((trip) =>
			(['Simple', 'Detailed'] as const).map((sntReport) => ({ tripId: trip.id, sntReport }))
		);
		let nextRequest = 0;
		const workers = Array.from({ length: Math.min(3, requests.length) }, async () => {
			while (nextRequest < requests.length) {
				const request = requests[nextRequest++];
				try {
					await getSntReportByTrip(request, true);
				} catch (error) {
					console.error('Failed to prefetch trucker SNT report:', error);
				}
			}
		});
		await Promise.all(workers);
	};

	const getCargosByTrip = async (payload: { cargoTypeResults: TCargoTypeResults; tripId: string }) => {
		const cacheKey = getTruckerCargosCacheKey(payload.tripId, payload.cargoTypeResults);
		const isOnline = await getConnectionStatus();
		const cachedCargos = isOnline ? null : await getTruckerBackgroundCache<IGetCargoByTrip>(cacheKey);

		if (!isOnline && isValidCargosResponse(cachedCargos)) {
			return { data: cachedCargos, status: 'success' };
		}

		const signal = abortRequest('getCargosByTrip');
		const { data, status } = await useCustomFetch<IGetCargoByTrip>(
			'/api/LongHaulTrip/get-cargos-by-trip',
			{
				query: payload,
				signal,
				timeout: 4000,
				watch: false,
				server: false
			},
			{ isSilent: false }
		);

		if (status.value === 'success' && isValidCargosResponse(data.value)) {
			try {
				await saveTruckerBackgroundCache(cacheKey, data.value);
			} catch (error) {
				console.error('Failed to save trucker cargos background cache:', error);
			}
		} else {
			const fallbackCargos =
				cachedCargos || (await getTruckerBackgroundCache<IGetCargoByTrip>(cacheKey));
			if (isValidCargosResponse(fallbackCargos)) {
				return { data: fallbackCargos, status: 'success' };
			}
		}

		return {
			data: data.value,
			status: status.value === 'success' && !isValidCargosResponse(data.value) ? 'error' : status.value
		};
	};

	const getDetailsByCargoInTrip = async (payload: { cargoId: string; tripId: string }) => {
		const cacheKey = getTruckerCargoDetailsCacheKey(payload.tripId, payload.cargoId);
		const isOnline = await getConnectionStatus();
		const cachedDetails = isOnline
			? null
			: await getTruckerBackgroundCache<IGetCargoInTripDetails>(cacheKey);
		if (isValidCargoDetailsResponse(cachedDetails)) {
			return { data: cachedDetails, status: 'success' };
		}

		const signal = abortRequest('getDetailsByCargoInTrip');
		const { data, status } = await useCustomFetch<IGetCargoInTripDetails>(
			'/api/LongHaulTrip/get-details-by-cargo-in-trip',
			{
				query: payload,
				signal,
				timeout: 4000,
				watch: false
			},
			{ isSilent: false }
		);

		if (status.value === 'success' && isValidCargoDetailsResponse(data.value)) {
			try {
				await saveTruckerBackgroundCache(cacheKey, data.value);
			} catch (error) {
				console.error('Failed to save trucker cargo details cache:', error);
			}
		} else {
			const fallbackDetails =
				cachedDetails || (await getTruckerBackgroundCache<IGetCargoInTripDetails>(cacheKey));
			if (isValidCargoDetailsResponse(fallbackDetails)) {
				return { data: fallbackDetails, status: 'success' };
			}
		}

		return {
			data: data.value,
			status: status.value === 'success' && !isValidCargoDetailsResponse(data.value) ? 'error' : status.value
		};
	};

	return {
		loading,
		headerInfo,
		tripsOnTheWay,
		$reset,
		getHeaderInfo,
		getTripsOnTheWay,
		getTripById,
		markCustomsPassed,
		getSntReportByTrip,
		prefetchSntReportsForTrips,
		setArriveTrip,
		setDeliveredCargo,
		getDetailsByCargoInTrip,
		getCargosByTrip,
		cancelRequest,
		clearTripsOnTheWayById
	};
});
